// src/pages/BlueButtonCallback.jsx
// Handles Blue Button OAuth callback - closes popup and notifies parent window
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export default function BlueButtonCallback() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Processing authorization...');

  useEffect(() => {
    const bluebutton = searchParams.get('bluebutton');
    const errorMessage = searchParams.get('message');
    const clientId = searchParams.get('clientId');

    console.log('[BlueButtonCallback] Received:', { bluebutton, clientId, errorMessage });

    // Store result in localStorage as backup (parent window will check this)
    const callbackResult = {
      type: 'bluebutton_callback',
      status: bluebutton === 'connected' ? 'connected' : 'error',
      message: errorMessage,
      clientId,
      timestamp: Date.now()
    };
    localStorage.setItem('bluebutton_callback_result', JSON.stringify(callbackResult));
    console.log('[BlueButtonCallback] Stored result in localStorage');

    if (bluebutton === 'connected') {
      setStatus('success');
      setMessage('Connected successfully! Closing...');

      // Try multiple ways to notify parent window
      notifyParent(callbackResult);

      // Close popup after brief delay
      setTimeout(() => {
        window.close();
        // If window.close() doesn't work (some browsers block it), show manual close message
        setMessage('Connected! You can close this window.');
      }, 1500);
    } else if (bluebutton === 'error') {
      setStatus('error');
      setMessage(errorMessage || 'Connection failed');

      // Notify parent window of error
      notifyParent(callbackResult);

      // Close popup after showing error
      setTimeout(() => {
        window.close();
        setMessage('You can close this window.');
      }, 3000);
    } else {
      // Unknown state - just close
      setTimeout(() => {
        window.close();
      }, 2000);
    }
  }, [searchParams]);

  // Try multiple methods to notify parent window
  const notifyParent = (result) => {
    // Method 1: postMessage to opener
    if (window.opener && !window.opener.closed) {
      try {
        window.opener.postMessage(result, '*'); // Use * for cross-origin compatibility
        console.log('[BlueButtonCallback] Sent postMessage to opener');
      } catch (e) {
        console.log('[BlueButtonCallback] postMessage failed:', e);
      }
    }

    // Method 2: BroadcastChannel (works across tabs/windows on same origin)
    try {
      const bc = new BroadcastChannel('bluebutton_auth');
      bc.postMessage(result);
      bc.close();
      console.log('[BlueButtonCallback] Sent via BroadcastChannel');
    } catch (e) {
      console.log('[BlueButtonCallback] BroadcastChannel not supported');
    }

    // Method 3: Storage event (localStorage change triggers event in other windows)
    // Already done above - the storage event will fire in other windows
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      fontFamily: 'system-ui, sans-serif',
      backgroundColor: status === 'error' ? '#fef2f2' : status === 'success' ? '#f0fdf4' : '#f9fafb',
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '40px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        textAlign: 'center',
        maxWidth: '400px'
      }}>
        {status === 'processing' && (
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #e5e7eb',
            borderTopColor: '#3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }} />
        )}

        {status === 'success' && (
          <div style={{
            width: '50px',
            height: '50px',
            backgroundColor: '#10b981',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px'
          }}>
            <svg width="24" height="24" fill="none" stroke="white" strokeWidth="3" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        )}

        {status === 'error' && (
          <div style={{
            width: '50px',
            height: '50px',
            backgroundColor: '#ef4444',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px'
          }}>
            <svg width="24" height="24" fill="none" stroke="white" strokeWidth="3" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        )}

        <h2 style={{
          fontSize: '20px',
          fontWeight: '600',
          color: status === 'error' ? '#991b1b' : status === 'success' ? '#166534' : '#374151',
          marginBottom: '10px'
        }}>
          {status === 'success' ? 'Medicare Connected!' :
           status === 'error' ? 'Connection Failed' :
           'Processing...'}
        </h2>

        <p style={{
          color: '#6b7280',
          fontSize: '14px'
        }}>
          {message}
        </p>

        {status !== 'processing' && (
          <p style={{
            color: '#9ca3af',
            fontSize: '12px',
            marginTop: '20px'
          }}>
            This window will close automatically
          </p>
        )}
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
